import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import MenuItemDetail from './MenuItemDetail';
import MealPlanModal from './MealPlanModal';
import cartService from '../services/cartService';
import guestCartService from '../services/guestCartService';
import SEO from './SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import {
  MEAL_TYPE_CATEGORIES,
  MEAL_TYPE_LABELS,
  COOKING_METHODS,
  COOKING_METHOD_LABELS,
  PROTEINS,
  PROTEIN_LABELS,
  itemProteins,
  itemCookingMethods,
  otherTagsFrom
} from '../constants/menuTaxonomy';
import './MenuFilter.css';
import { useDebounce } from 'use-debounce';
import { resolveHasLiked, resolveLikesCount } from '../utils/menuLikes';

// Helper function to parse time strings into minutes
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const match = timeStr.match(/(\d+)\s*(hour|hr|h|minute|min|m)?s?\s*/gi);
  if (!match) return 0;

  let totalMinutes = 0;
  match.forEach(part => {
    const [_, num, unit] = part.match(/(\d+)\s*(hour|hr|h|minute|min|m)?/i) || [];
    if (num) {
      if (unit && unit.toLowerCase().startsWith('h')) {
        totalMinutes += parseInt(num) * 60;
      } else {
        totalMinutes += parseInt(num);
      }
    }
  });
  return totalMinutes;
};

const SORT_OPTIONS = [
  { label: 'Recently Added', value: 'recent' },
  { label: 'Most Popular', value: 'popular' },
  { label: 'Most Liked', value: 'likes' },
  { label: 'Quickest to Make', value: 'quick' },
  { label: 'Name (A–Z)', value: 'title' }
];

// Sort a list of menu items by the chosen key. Operates on the already-fetched
// list (the web menu loads all items), so sorting is instant and works alongside
// the existing client-side filters.
const sortMenuItems = (items, sortBy) => {
  const arr = [...items];
  const likeCount = (it) => (Array.isArray(it.likes) ? it.likes.length : (it.likes || 0));
  const popularity = (it) => (it.metrics?.views || 0);
  const createdMs = (it) => (it.createdAt ? new Date(it.createdAt).getTime() : 0);
  const cookMins = (it) => {
    const m = parseTimeToMinutes(it.totalTime);
    return m > 0 ? m : Number.POSITIVE_INFINITY; // untimed dishes sort last
  };

  switch (sortBy) {
    case 'popular':
      return arr.sort((a, b) => popularity(b) - popularity(a) || createdMs(b) - createdMs(a));
    case 'likes':
      return arr.sort((a, b) => likeCount(b) - likeCount(a) || createdMs(b) - createdMs(a));
    case 'quick':
      return arr.sort((a, b) => cookMins(a) - cookMins(b));
    case 'title':
      return arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    case 'recent':
    default:
      return arr.sort((a, b) => createdMs(b) - createdMs(a));
  }
};

const MenuFilter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [filters, setFilters] = useState({
    dietary: [],
    cuisine: '',
    cookTime: '',
    cookingMethods: [],
    categories: [],
    proteins: [],
    otherTags: []
  });
  const [timeRange, setTimeRange] = useState({ min: 0, max: 180 }); // Default range 0-180 minutes
  const [selectedTimeRange, setSelectedTimeRange] = useState([0, 180]); // Default selected range
  const [selectedMealItem, setSelectedMealItem] = useState(null);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [sortBy, setSortBy] = useState('recent');
  const [cuisineOptions, setCuisineOptions] = useState([]);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [cookingMethodsOptions, setCookingMethodsOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [proteinOptions, setProteinOptions] = useState([]);
  const [otherTagsOptions, setOtherTagsOptions] = useState([]);
  const itemsPerPage = 20;
  const [visibleItemsCount, setVisibleItemsCount] = useState(itemsPerPage);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const observerTarget = useRef(null);

  // Deep links like /menu?search=Thai%20Green%20Curry (used by the landing
  // page showcase cards) land with the search already applied.
  useEffect(() => {
    const searchParam = new URLSearchParams(window.location.search).get('search');
    if (searchParam) setSearchTerm(searchParam);
  }, []);

  // Add predefined cook time options
  const cookTimeOptions = [
    { label: 'Less than 15 min', value: '15' },
    { label: 'Less than 30 min', value: '30' },
    { label: 'Less than 45 min', value: '45' },
    { label: 'Less than 60 min', value: '60' },
    { label: 'Less than 90 min', value: '90' },
    { label: 'Any time', value: '' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const urlParams = new URLSearchParams(window.location.search);
        const affiliateId = urlParams.get('affiliate');

        let menuEndpoint = `${process.env.REACT_APP_API_URL}/menu`;
        if (affiliateId) {
          menuEndpoint += `?affiliate=${affiliateId}`;
        }

        const authToken = localStorage.getItem('token');
        const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const [menuResponse, cuisinesResponse, dietaryResponse, tagsResponse] = await Promise.all([
          fetch(menuEndpoint, { headers: authHeaders }),
          fetch(`${process.env.REACT_APP_API_URL}/menu/cuisines`),
          fetch(`${process.env.REACT_APP_API_URL}/menu/dietary-options`),
          fetch(`${process.env.REACT_APP_API_URL}/menu/tags`)
        ]);
        
        if (!menuResponse.ok || !cuisinesResponse.ok || !dietaryResponse.ok || !tagsResponse.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const [menuResponseData, cuisinesData, dietary, allTags] = await Promise.all([
          menuResponse.json(),
          cuisinesResponse.json(),
          dietaryResponse.json(),
          tagsResponse.json()
        ]);
        
        // Handle both array and object response formats
        const menuData = menuResponseData.menuItems || menuResponseData || [];
        const cuisines = cuisinesData.cuisines || cuisinesData || [];
        
        // Closed-set chips come from the recipe fields (or a conservative
        // fallback), not from substring-matching free-form tags. That is why
        // the edit form and this panel can share the same lists.
        const categories = MEAL_TYPE_CATEGORIES.filter(category =>
          menuData.some(item => item.categories?.includes(category))
        );

        const cookingMethods = COOKING_METHODS.filter(method =>
          menuData.some(item => itemCookingMethods(item).includes(method))
        );

        const proteins = PROTEINS.filter(protein =>
          menuData.some(item => itemProteins(item).includes(protein))
        );

        const otherTags = otherTagsFrom(allTags, dietary)
          .sort((a, b) => a.localeCompare(b));

        // Calculate min and max times from menu items
        const times = Array.isArray(menuData) ? menuData.map(item => parseTimeToMinutes(item.totalTime)).filter(time => time > 0) : [];
        const minTime = Math.min(...times);
        const maxTime = Math.max(...times);
        
        // Sort menu items by createdAt descending (newest first), fallback to updatedAt if missing
        if (Array.isArray(menuData)) {
          menuData.sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : (a.updatedAt ? new Date(a.updatedAt) : new Date(0));
            const dateB = b.createdAt ? new Date(b.createdAt) : (b.updatedAt ? new Date(b.updatedAt) : new Date(0));
            return dateB - dateA;
          });
        }
        
        setTimeRange({ min: minTime, max: maxTime });
        setSelectedTimeRange([minTime, maxTime]);
        setMenuItems(menuData);
        setFilteredMenuItems(menuData);
        setCuisineOptions(cuisines.filter(Boolean));
        setDietaryOptions(dietary.filter(Boolean));
        setCookingMethodsOptions(cookingMethods);
        setCategoriesOptions(categories);
        setProteinOptions(proteins);
        setOtherTagsOptions(otherTags);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Generate search suggestions based on search term
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const suggestions = [];
      const searchLower = searchTerm.toLowerCase();
      
      // Add matching dietary options
      dietaryOptions.forEach(option => {
        if (option.toLowerCase().includes(searchLower) && !suggestions.includes(option)) {
          suggestions.push({ text: option, type: 'dietary' });
        }
      });
      
      // Add matching cuisine options
      cuisineOptions.forEach(option => {
        if (option.toLowerCase().includes(searchLower) && !suggestions.find(s => s.text === option)) {
          suggestions.push({ text: option, type: 'cuisine' });
        }
      });
      
      // Add matching cooking methods
      cookingMethodsOptions.forEach(option => {
        if (option.toLowerCase().includes(searchLower) && !suggestions.find(s => s.text === option)) {
          suggestions.push({ text: option, type: 'cooking method' });
        }
      });
      
      // Add matching categories
      categoriesOptions.forEach(option => {
        const label = MEAL_TYPE_LABELS[option] || option;
        if (label.toLowerCase().includes(searchLower) && !suggestions.find(s => s.text === label)) {
          suggestions.push({ text: label, type: 'category' });
        }
      });

      proteinOptions.forEach(option => {
        const label = PROTEIN_LABELS[option] || option;
        if (label.toLowerCase().includes(searchLower) && !suggestions.find(s => s.text === label)) {
          suggestions.push({ text: label, type: 'protein' });
        }
      });
      
      setSearchSuggestions(suggestions.slice(0, 5)); // Limit to 5 suggestions
    } else {
      setSearchSuggestions([]);
    }
  }, [searchTerm, dietaryOptions, cuisineOptions, cookingMethodsOptions, categoriesOptions, proteinOptions]);

  useEffect(() => {
    const filtered = menuItems.filter(item => {
      // Dietary preferences filter (multiple selection)
      const matchesDietary = filters.dietary.length === 0 || 
        filters.dietary.every(filterValue => 
          item.dietary?.some(itemTag => 
            (itemTag || '').toLowerCase() === (filterValue || '').toLowerCase()
          )
        );

      // Cuisine filter (single selection)
      const matchesCuisine = !filters.cuisine || 
        (item.cuisine && item.cuisine.toLowerCase() === filters.cuisine.toLowerCase());
      
      // Cook time filter
      const itemTime = parseTimeToMinutes(item.totalTime);
      const matchesTime = !filters.cookTime || itemTime <= parseInt(filters.cookTime);
      
      // Cooking methods filter (multiple selection)
      const matchesCookingMethods = filters.cookingMethods.length === 0 ||
        filters.cookingMethods.some(method => itemCookingMethods(item).includes(method));
      
      // Categories filter (multiple selection). Older recipes predate the
      // `categories` field, so fall back to their tags.
      const itemCategories = item.categories?.length ? item.categories : (item.tags || []);
      const matchesCategories = filters.categories.length === 0 ||
        filters.categories.some(category =>
          itemCategories.some(value => String(value).toLowerCase() === category.toLowerCase())
        );

      const matchesProteins = filters.proteins.length === 0 ||
        filters.proteins.some(protein => itemProteins(item).includes(protein));

      // Other tags filter (multiple selection)
      const matchesOtherTags = filters.otherTags.length === 0 ||
        filters.otherTags.some(tag =>
          item.tags?.some(value => String(value).toLowerCase() === tag.toLowerCase())
        );

      // Search term filter - enhanced to include dietary tags, cuisine, and other tags
      const searchLower = debouncedSearchTerm.toLowerCase();
      const matchesSearch = !debouncedSearchTerm || 
        item.title.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        (item.ingredients && item.ingredients.some(ingredient => 
          (typeof ingredient === 'object' && ingredient.name 
            ? ingredient.name.toLowerCase().includes(searchLower)
            : String(ingredient).toLowerCase().includes(searchLower))
        )) ||
        (item.dietary && item.dietary.some(diet => diet.toLowerCase().includes(searchLower))) ||
        (item.cuisine && item.cuisine.toLowerCase().includes(searchLower)) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchLower)));

      return matchesDietary && matchesCuisine && matchesTime && matchesCookingMethods
        && matchesCategories && matchesProteins && matchesOtherTags && matchesSearch;
    });
    
    setFilteredMenuItems(sortMenuItems(filtered, sortBy));
    setVisibleItemsCount(itemsPerPage);
    setLoadingMore(false);
  }, [filters, menuItems, debouncedSearchTerm, sortBy]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (Array.isArray(prevFilters[filterType])) {
        // For array filters, toggle the value
        const newArray = prevFilters[filterType].includes(value)
          ? prevFilters[filterType].filter(item => item !== value)
          : [...prevFilters[filterType], value];
        return { ...prevFilters, [filterType]: newArray };
      } else {
        // For single value filters (cuisine, cookTime), just set the value
        return { ...prevFilters, [filterType]: value };
      }
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleLike = async (e, menuItemId) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to like menu items');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token missing. Please sign in again.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${menuItemId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        toast.error('Your session has expired. Please sign in again.');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        window.location.reload();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to like menu item');
      }

      const data = await response.json();
      
      // Update both menuItems and filteredMenuItems
      const updateItems = (items) => items.map(item => 
        item._id === menuItemId 
          ? { 
              ...item, 
              likes: data.likes,
              isLiked: data.hasLiked
            }
          : item
      );

      setMenuItems(updateItems);
      setFilteredMenuItems(prev => updateItems(prev));

      // If this item is currently selected in detail view, update it too
      if (selectedItem?._id === menuItemId) {
        setSelectedItem(prev => ({
          ...prev,
          likes: data.likes,
          isLiked: data.hasLiked
        }));
      }
    } catch (error) {
      console.error('Error liking menu item:', error);
      toast.error('Failed to like menu item. Please try again.');
    }
  };

  const addSelectedMenuItemToShoppingList = async () => {
    if (user) {
      await cartService.addMenuToCart(selectedMealItem._id);
    } else {
      await guestCartService.addMenuToCart(selectedMealItem._id, [], null, false, false, selectedMealItem);
    }

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/menu/${selectedMealItem._id}/metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incrementCartAdds: 1 })
      });
    } catch (metricsError) {
      console.error('Failed to update cart metrics:', metricsError);
    }
  };

  const handleAddToMealPlan = async (date, mealType, options = {}) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/mealplan/${user.sub}/meals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          menuItemId: selectedMealItem._id,
          date,
          mealType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'source_mismatch') {
          toast.error(errorData.message, { duration: 5000 });
          return { success: false };
        }
        throw new Error(errorData.error || 'Failed to add meal to plan');
      }

      if (options.addToShoppingList) {
        await addSelectedMenuItemToShoppingList();
      }

      return {
        success: true,
        addedToShoppingList: Boolean(options.addToShoppingList)
      };
    } catch (error) {
      console.error('Error adding to meal plan:', error);
      toast.error('Failed to add to meal plan. Please try again.');
      return { success: false };
    }
  };

  const handleAddToPlanClick = async (e, item) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to add to meal plan');
      return;
    }
    
    // Check menu source compatibility before showing modal
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/mealplan/${user.sub}/check-menu-source/${item._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (!data.canAdd) {
          // Show the source conflict message
          toast.error(data.message, { duration: 5000 });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking menu source:', error);
      // Continue anyway if check fails
    }
    
    setSelectedMealItem(item);
    setShowMealPlanModal(true);
  };

  const visibleItems = filteredMenuItems.slice(0, visibleItemsCount);
  const hasMore = visibleItemsCount < filteredMenuItems.length;

  useEffect(() => {
    if (!hasMore || loading || error) {
      return undefined;
    }

    const target = observerTarget.current;
    if (!target) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMore) {
          setLoadingMore(true);
          window.setTimeout(() => {
            setVisibleItemsCount((previousCount) => Math.min(previousCount + itemsPerPage, filteredMenuItems.length));
            setLoadingMore(false);
          }, 150);
        }
      },
      {
        root: null,
        rootMargin: '160px 0px',
        threshold: 0.1
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [error, filteredMenuItems.length, hasMore, itemsPerPage, loading, loadingMore]);

  // Effect to handle deep linking
  useEffect(() => {
    const fetchMenuItem = async () => {
      if (id) {
        try {
          // First try to find the item in the existing menuItems
          if (menuItems.length > 0) {
            const item = menuItems.find(item => item._id === id);
            if (item) {
              setSelectedItem(item);
              return;
            }
          }
          
          // If not found or menuItems is empty, fetch it directly
          const authToken = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${id}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch menu item: ${response.status} ${response.statusText}`);
          }
          const item = await response.json();
          
          if (!item || !item._id) {
            throw new Error('Invalid menu item data received from API');
          }
          
          // Transform the item to match the format of other menu items
          const transformedItem = {
            ...item,
            likes: resolveLikesCount(item),
            isLiked: resolveHasLiked(item, user)
          };
          
          setSelectedItem(transformedItem);
        } catch (error) {
          console.error('Error in deep linking:', error);
          setError(`Failed to load menu item details: ${error.message}`);
          setSelectedItem(null);
        }
      } else {
        // Clear selected item when no ID is present
        setSelectedItem(null);
      }
    };

    fetchMenuItem();
  }, [id, menuItems, user]);

  // Update URL when a menu item is selected or closed
  const handleSelectMenuItem = async (item) => {
    // Ensure the item has the latest likes data
    const updatedItem = menuItems.find(menuItem => menuItem._id === item._id) || item;
    setSelectedItem(updatedItem);
    
    // Get affiliate ID from URL if it exists
    const urlParams = new URLSearchParams(window.location.search);
    const affiliateId = urlParams.get('affiliate');
    
    // Construct the URL with affiliate ID if it exists
    const baseUrl = `/menu/${item._id}`;
    const url = affiliateId ? `${baseUrl}?affiliate=${affiliateId}` : baseUrl;
    
    navigate(url, { replace: true });

    // Track view using new metrics endpoint
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/menu/${item._id}/metrics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            incrementViews: 1
          })
        });
      } catch (error) {
        console.error('Error tracking menu item view:', error);
      }
    }
  };

  const handleCloseMenuItem = () => {
    setSelectedItem(null);
    const urlParams = new URLSearchParams(window.location.search);
    const context = urlParams.get('context');
    const affiliateId = urlParams.get('affiliate');

    // Determine where to navigate based on context
    let url;
    if (context === 'admin') {
      url = '/admin/dashboard';
    } else if (context === 'affiliate') {
      url = '/affiliate/dashboard';
    } else {
      // Default to menu page with affiliate parameter if it exists
      url = affiliateId ? `/menu?affiliate=${affiliateId}` : '/menu';
    }

    navigate(url, { replace: true });
  };

  const handleDietaryToggle = (option) => {
    const currentPreferences = filters.dietary || [];
    const newPreferences = currentPreferences.includes(option)
      ? currentPreferences.filter(pref => pref !== option)
      : [...currentPreferences, option];
    
    setFilters(prev => ({
      ...prev,
      dietary: newPreferences
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      dietary: [],
      cuisine: '',
      cookTime: '',
      cookingMethods: [],
      categories: [],
      proteins: [],
      otherTags: []
    });
  };

  const activeFilterCount = filters.dietary.length
    + (filters.cuisine ? 1 : 0)
    + (filters.cookTime ? 1 : 0)
    + filters.cookingMethods.length
    + filters.categories.length
    + filters.proteins.length
    + filters.otherTags.length;

  const hasActiveFilters = activeFilterCount > 0;

  const seo = PAGE_SEO['/menu'];
  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seo.title,
    description: seo.description,
    url: 'https://freshfitlab.io/menu',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filteredMenuItems.length,
      itemListElement: filteredMenuItems.slice(0, 20).map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://freshfitlab.io/menu/${item.menuId || item._id}`,
        name: item.title,
      })),
    },
  };

  return (
    <div className={`menu-filter${isFilterPanelOpen ? ' filter-panel-open' : ''}`}>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/menu"
        jsonLd={[
          collectionJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Recipes', url: '/menu' },
          ]),
        ]}
      />
      {loading && <div className="loading-state" aria-busy="true">Loading menu items...</div>}
      {error && <div className="error">Error: {error}</div>}
      
      <div className="menu-filter-container">
        {/* Search bar gets its own row so the query text stays fully visible */}
        <div className="menu-search-row">
          <div className="menu-search-container">
            <i className="fas fa-search menu-search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, ingredients, or filters (e.g., dairy-free)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="menu-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="menu-search-clear-btn"
                aria-label="Clear search"
                onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="search-suggestions">
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => {
                      setSearchTerm(suggestion.text);
                      setShowSuggestions(false);
                    }}
                  >
                    <span className="suggestion-text">{suggestion.text}</span>
                    <span className="suggestion-type">{suggestion.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sort and Filters live on their own row so they no longer crowd search */}
        <div className="menu-controls-row">
          <div className="menu-sort-control">
            <label htmlFor="menu-sort-select" className="menu-sort-label">
              <i className="fas fa-arrow-down-wide-short"></i>
              <span>Sort by</span>
            </label>
            <select
              id="menu-sort-select"
              className="menu-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button 
            className={`filter-panel-trigger ${hasActiveFilters ? 'has-filters' : ''}`}
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
          >
            <i className="fas fa-sliders-h"></i>
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="filter-count">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Clear Filters Button (if active) */}
        {hasActiveFilters && (
          <div className="clear-filters-container">
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              <i className="fas fa-times"></i>
              <span>Clear All</span>
            </button>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="active-filters">
            {filters.cuisine && (
              <div className="filter-chip">
                <i className="fas fa-globe"></i>
                <span>{filters.cuisine}</span>
                <button onClick={() => handleFilterChange('cuisine', '')}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
            {filters.dietary.map(diet => (
              <div key={diet} className="filter-chip">
                <i className="fas fa-leaf"></i>
                <span>{diet}</span>
                <button onClick={() => handleDietaryToggle(diet)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            {filters.cookTime && (
              <div className="filter-chip">
                <i className="fas fa-clock"></i>
                <span>Under {filters.cookTime} min</span>
                <button onClick={() => handleFilterChange('cookTime', '')}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
            {filters.cookingMethods.map(method => (
              <div key={method} className="filter-chip">
                <i className="fas fa-fire"></i>
                <span>{COOKING_METHOD_LABELS[method] || method}</span>
                <button onClick={() => handleFilterChange('cookingMethods', method)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            {filters.categories.map(category => (
              <div key={category} className="filter-chip">
                <i className="fas fa-utensils"></i>
                <span>{MEAL_TYPE_LABELS[category] || category}</span>
                <button onClick={() => handleFilterChange('categories', category)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            {filters.proteins.map(protein => (
              <div key={protein} className="filter-chip">
                <i className="fas fa-drumstick-bite"></i>
                <span>{PROTEIN_LABELS[protein] || protein}</span>
                <button onClick={() => handleFilterChange('proteins', protein)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            {filters.otherTags.map(tag => (
              <div key={tag} className="filter-chip">
                <i className="fas fa-tag"></i>
                <span>{tag}</span>
                <button onClick={() => handleFilterChange('otherTags', tag)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Filter Panel */}
        {isFilterPanelOpen && (
          <>
            <div className="filter-panel-overlay" onClick={() => setIsFilterPanelOpen(false)} />
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3>Filter Options</h3>
                <button className="close-panel-btn" onClick={() => setIsFilterPanelOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="filter-panel-content">
                {/* Cuisine Section */}
                <div className="filter-section">
                  <h4>
                    <i className="fas fa-globe"></i>
                    Cuisine
                  </h4>
                  <div className="filter-chips">
                    <button
                      className={`chip ${!filters.cuisine ? 'active' : ''}`}
                      onClick={() => handleFilterChange('cuisine', '')}
                    >
                      All Cuisines
                    </button>
                    {cuisineOptions
                      .filter(option => option && typeof option === 'string')
                      .map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.cuisine === option ? 'active' : ''}`}
                          onClick={() => handleFilterChange('cuisine', filters.cuisine === option ? '' : option)}
                        >
                          {option}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Dietary Preferences Section */}
                <div className="filter-section">
                  <h4>
                    <i className="fas fa-leaf"></i>
                    Dietary Preferences
                  </h4>
                  <div className="filter-chips">
                    {dietaryOptions
                      .filter(option => option && typeof option === 'string')
                      .map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.dietary.includes(option) ? 'active' : ''}`}
                          onClick={() => handleDietaryToggle(option)}
                        >
                          {filters.dietary.includes(option) && <i className="fas fa-check"></i>}
                          {option}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Cook Time Section */}
                <div className="filter-section">
                  <h4>
                    <i className="fas fa-clock"></i>
                    Cooking Time
                  </h4>
                  <div className="filter-chips">
                    {cookTimeOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`chip ${filters.cookTime === option.value ? 'active' : ''}`}
                        onClick={() => handleFilterChange('cookTime', filters.cookTime === option.value ? '' : option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cooking Methods Section */}
                {cookingMethodsOptions.length > 0 && (
                  <div className="filter-section">
                    <h4>
                      <i className="fas fa-fire"></i>
                      Cooking Methods
                    </h4>
                    <div className="filter-chips">
                      {cookingMethodsOptions.map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.cookingMethods.includes(option) ? 'active' : ''}`}
                          onClick={() => handleFilterChange('cookingMethods', option)}
                        >
                          {filters.cookingMethods.includes(option) && <i className="fas fa-check"></i>}
                          {COOKING_METHOD_LABELS[option] || option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories Section */}
                {categoriesOptions.length > 0 && (
                  <div className="filter-section">
                    <h4>
                      <i className="fas fa-utensils"></i>
                      Categories
                    </h4>
                    <div className="filter-chips">
                      {categoriesOptions.map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.categories.includes(option) ? 'active' : ''}`}
                          onClick={() => handleFilterChange('categories', option)}
                        >
                          {filters.categories.includes(option) && <i className="fas fa-check"></i>}
                          {MEAL_TYPE_LABELS[option] || option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {proteinOptions.length > 0 && (
                  <div className="filter-section">
                    <h4>
                      <i className="fas fa-drumstick-bite"></i>
                      Protein
                    </h4>
                    <div className="filter-chips">
                      {proteinOptions.map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.proteins.includes(option) ? 'active' : ''}`}
                          onClick={() => handleFilterChange('proteins', option)}
                        >
                          {filters.proteins.includes(option) && <i className="fas fa-check"></i>}
                          {PROTEIN_LABELS[option] || option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Tags Section — free-form descriptors that are not a
                    meal type, cooking method, protein, or dietary label. */}
                {otherTagsOptions.length > 0 && (
                  <div className="filter-section">
                    <h4>
                      <i className="fas fa-tag"></i>
                      Other Tags
                    </h4>
                    <div className="filter-chips">
                      {otherTagsOptions.map((option) => (
                        <button
                          key={option}
                          className={`chip ${filters.otherTags.includes(option) ? 'active' : ''}`}
                          onClick={() => handleFilterChange('otherTags', option)}
                        >
                          {filters.otherTags.includes(option) && <i className="fas fa-check"></i>}
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-panel-footer">
                <button className="apply-filters-btn" onClick={() => setIsFilterPanelOpen(false)}>
                  <i className="fas fa-check"></i>
                  Apply Filters
                </button>
              </div>
            </div>
          </>
        )}

        {!loading && !error && filteredMenuItems.length === 0 && (
          <div className="no-results" role="status">
            <p>No recipes match your search or filters.</p>
            {hasActiveFilters && (
              <button type="button" className="clear-filters-btn" onClick={handleClearFilters}>
                Clear all filters
              </button>
            )}
          </div>
        )}

        {!loading && !error && filteredMenuItems.length > 0 && (
          <>
            <div className="menu-results-summary">
              <span>
                Showing {visibleItems.length} of {filteredMenuItems.length} menu items
              </span>
            </div>
            <div className="menu-cards">
              {visibleItems.map(item => (
                <div 
                  className="menu-card" 
                  key={item._id}
                  onClick={() => handleSelectMenuItem(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="menu-image"
                  />
                  <div className="menu-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    {item.creatorInfo?.slug && (
                      <a
                        className="creator-badge"
                        href={`/creator/${item.creatorInfo.slug}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fas fa-user-circle"></i> {item.creatorInfo.name}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <div className="menu-load-more" ref={observerTarget}>
                {loadingMore ? 'Loading more menu items...' : 'Scroll for more menu items'}
              </div>
            )}
            {!hasMore && visibleItems.length > itemsPerPage && (
              <div className="menu-load-more complete">You have reached the end of the menu list.</div>
            )}
          </>
        )}

        {selectedItem && (
          <MenuItemDetail
            item={selectedItem}
            onClose={handleCloseMenuItem}
          />
        )}

        {showMealPlanModal && selectedMealItem && (
          <MealPlanModal
            menuItem={selectedMealItem}
            onClose={() => {
              setShowMealPlanModal(false);
              setSelectedMealItem(null);
            }}
            onSave={handleAddToMealPlan}
          />
        )}
      </div>
    </div>
  );
};

export default MenuFilter; 